import { createContext, useContext } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  MODULE_CONFIG,
  MODULE_DEPT_KEYS,
  PARENT_PATH_OVERRIDES,
  PAGE_LABEL_OVERRIDES,
  TAB_LABEL_MAP,
  DIRECT_LABEL_OVERRIDES,
  VIEW_TAB_EXCEPTIONS,
} from '../config/breadcrumbConfig';
import { breadcrumbStrings } from '../components/StringBundle';
import { BreadcrumbCrumb, CRUMB_LABELS, RouteLookupEntry, StepLabelOverrides, StoredCrumb, TrailCrumb } from '../screens/admin/types';
import {
  isIdSegment, toTitleCase, singularize, paramsFromPath, splitDestination,
  normalizePathname, expandCrumb, tagKinds, findNextNonId,
} from '../constants/sidebarConstants';

export { paramsFromPath, splitDestination } from '../constants/sidebarConstants';

export const DEFAULT_TAB_LABEL_EVENT = 'ccl:defaultTabLabelReady';
export const BREADCRUMB_TRAIL_STORAGE_KEY = 'ccl:breadcrumbTrail';


const TAB_LABELS_KEY = 'ccl:tabLabels';
const DEFAULT_LABELS_KEY = 'ccl:defaultTabLabels';
const STEP_LABELS_KEY = 'ccl:stepLabels';

let tabLabelsCache: Record<string, string> | null = null;
let defaultLabelsCache: Record<string, string> | null = null;
let stepLabelsCache: Record<string, StepLabelOverrides> | null = null;

function getTabLabelsCache(): Record<string, string> {
  if (tabLabelsCache !== null) return tabLabelsCache;
  try {
    const raw = sessionStorage.getItem(TAB_LABELS_KEY);
    tabLabelsCache = (raw ? JSON.parse(raw) : {}) as Record<string, string>;
  } catch { tabLabelsCache = {}; }
  return tabLabelsCache;
}

function getDefaultLabelsCache(): Record<string, string> {
  if (defaultLabelsCache !== null) return defaultLabelsCache;
  try {
    const raw = sessionStorage.getItem(DEFAULT_LABELS_KEY);
    defaultLabelsCache = (raw ? JSON.parse(raw) : {}) as Record<string, string>;
  } catch { defaultLabelsCache = {}; }
  return defaultLabelsCache;
}

function getStepLabelsCache(): Record<string, StepLabelOverrides> {
  if (stepLabelsCache !== null) return stepLabelsCache;
  try {
    const raw = sessionStorage.getItem(STEP_LABELS_KEY);
    stepLabelsCache = (raw ? JSON.parse(raw) : {}) as Record<string, StepLabelOverrides>;
  } catch { stepLabelsCache = {}; }
  return stepLabelsCache;
}

// ── Session-storage helpers ───────────────────────────────────────────────────

export function writeDefaultTabLabel(pathname: string, label: string): void {
  if (!pathname || !label) return;
  const normKey = normalizePathname(pathname);
  const cache = getDefaultLabelsCache();
  if (cache[normKey] !== label) {
    cache[normKey] = label;
    try { sessionStorage.setItem(DEFAULT_LABELS_KEY, JSON.stringify(cache)); } catch { }
  }
  try {
    document.dispatchEvent(new CustomEvent(DEFAULT_TAB_LABEL_EVENT, { detail: { pathname, label } }));
  } catch { /* no-op outside DOM */ }
}

export function writeTabLabel(pathname: string, tabIndex: string, label: string): void {
  if (!pathname || !tabIndex || !label) return;
  // Standard tabs are resolved from TAB_LABEL_MAP — no need to store them.
  if (Object.prototype.hasOwnProperty.call(TAB_LABEL_MAP, tabIndex)) return;
  const entryKey = `${normalizePathname(pathname)}:${tabIndex}`;
  const cache = getTabLabelsCache();
  if (cache[entryKey] !== label) {
    cache[entryKey] = label;
    try { sessionStorage.setItem(TAB_LABELS_KEY, JSON.stringify(cache)); } catch { }
  }
}

export function readTabLabel(pathname: string, tabIndex: string | null): string | null {
  if (!pathname || !tabIndex) return null;
  return getTabLabelsCache()[`${normalizePathname(pathname)}:${tabIndex}`] || null;
}

export function writeStepLabelOverrides(routeSegment: string, overrides: StepLabelOverrides): void {
  if (!routeSegment || !overrides) return;
  const cache = getStepLabelsCache();
  cache[routeSegment] = { ...cache[routeSegment], ...overrides };
  try { sessionStorage.setItem(STEP_LABELS_KEY, JSON.stringify(cache)); } catch { }
}

function readStepLabelOverrides(routeSegment: string): StepLabelOverrides | undefined {
  return getStepLabelsCache()[routeSegment];
}

export function readDefaultTabLabel(pathname: string): string | null {
  return getDefaultLabelsCache()[normalizePathname(pathname || '')] || null;
}

export function readStoredTrail(): TrailCrumb[] {
  try {
    const raw = sessionStorage.getItem(BREADCRUMB_TRAIL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(expandCrumb);
  } catch {
    return [];
  }
}

export function writeStoredTrail(trail: TrailCrumb[]): void {
  try {
    // Store only the minimal fields; derived fields are recomputed on read.
    const slim: StoredCrumb[] = trail.map(({ label, to, pagePath, deptKey, kind, tab, state }) => {
      const c: StoredCrumb = { label, to, pagePath };
      if (deptKey != null) c.deptKey = deptKey;
      if (kind) c.kind = kind;
      if (tab) c.tab = tab;
      if (state && Object.keys(state).length > 0) c.state = state;
      return c;
    });
    sessionStorage.setItem(BREADCRUMB_TRAIL_STORAGE_KEY, JSON.stringify(slim));
  } catch { /* sessionStorage unavailable */ }
}


export const BreadcrumbTrailContext = createContext<TrailCrumb[]>([]);

// Single nested loop: eliminates intermediate allRoutes array. First occurrence wins.
const routeLookup: Record<string, RouteLookupEntry> = {};
for (const { moduleLabel, routes } of MODULE_CONFIG) {
  for (const { label: pageLabel, to } of routes) {
    const key = to?.toLowerCase();
    if (key && !routeLookup[key]) routeLookup[key] = { moduleLabel, pageLabel };
  }
}

const LINE_ITEM_BASE_PATHS: Record<string, string> = {
  '/quotation-item': breadcrumbStrings.quotationItem,
};

const DOUBLE_ID_LINE_ITEM_MODULES: Record<string, (t: string | null) => string> = {
  '/pre-shipment-sample': (t) => {
    if (t === 'pss') return breadcrumbStrings.preShipmentSampleLineItem;
    if (t === 'sample') return breadcrumbStrings.typeSampleLineItem;
    return breadcrumbStrings.lineItem;
  },
};

const LINE_ITEM_SEGMENTS = new Set<string>(['sample-item']);

function tabToLabel(tabId: string | null, pathname: string, defaultTabLabel: string | null): string | null {
  if (!tabId) return defaultTabLabel;

  if (Object.prototype.hasOwnProperty.call(TAB_LABEL_MAP, tabId)) {
    const mapped = TAB_LABEL_MAP[tabId];
    return mapped !== null ? mapped : defaultTabLabel;
  }

  const stored = readTabLabel(pathname, tabId);
  if (stored) return stored;

  return tabId.includes(' ') ? tabId : toTitleCase(tabId);
}

function resolvePageLabel(
  basePath: string, lookupPath: string,
  pageLabel: string, rest: string[], typeParam: string | null,
): string {
  // Line-item sub-resources take priority: their basePath is more specific than
  // the parent route's lookupPath that PAGE_LABEL_OVERRIDES is keyed on.
  if (LINE_ITEM_BASE_PATHS[basePath]) return LINE_ITEM_BASE_PATHS[basePath];
  if (PAGE_LABEL_OVERRIDES[lookupPath]) return PAGE_LABEL_OVERRIDES[lookupPath];
  if (DOUBLE_ID_LINE_ITEM_MODULES[basePath]) {
    if (rest.filter(s => s === ':id').length >= 2)
      return DOUBLE_ID_LINE_ITEM_MODULES[basePath](typeParam);
  }
  return pageLabel;
}

/** Walks rest[] and pushes action / sub-resource crumbs onto the array. */
function appendSegmentCrumbs(crumbs: Omit<BreadcrumbCrumb, 'kind'>[], rest: string[]): void {
  const { actionCreate, actionView, actionEdit, actionDelete, actionAdd } = CRUMB_LABELS;
  const ACTION_LABEL: Record<string, string> = {
    create: actionCreate, Create: actionCreate,
    edit: actionEdit, delete: actionDelete,
    add: actionAdd, Add: actionAdd,
  };
  const VIEW_SEGS = new Set(['view', 'detail', 'Detail']);
  let viewAdded = false;

  for (let i = 0; i < rest.length; i++) {
    const seg = rest[i];
    if (seg === ':id') continue;

    if (DIRECT_LABEL_OVERRIDES[seg]) { crumbs.push({ label: DIRECT_LABEL_OVERRIDES[seg], to: null }); continue; }
    if (VIEW_SEGS.has(seg)) { crumbs.push({ label: actionView, to: null }); viewAdded = true; continue; }
    if (ACTION_LABEL[seg]) {
      const pageLabel = crumbs.length >= 2 ? crumbs[1].label : null;
      const isCreateOrEdit = seg === 'create' || seg === 'Create' || seg === 'edit';
      const baseActionLabel = ACTION_LABEL[seg];
      const label = isCreateOrEdit && pageLabel ? `${baseActionLabel} ${singularize(pageLabel)}` : baseActionLabel;
      crumbs.push({ label, to: null });
      return;
    }

    if (LINE_ITEM_SEGMENTS.has(seg)) {
      const next = findNextNonId(rest, i);
      const isCreate = next === 'create' || next === 'Create';
      const isEdit = next === 'edit';
      if (isCreate || isEdit) {
        // Creating/editing the line item itself — a single merged crumb, no separate "View".
        crumbs.push({ label: `${isCreate ? actionCreate : actionEdit} ${breadcrumbStrings.sampleLineItem}`, to: null });
      } else {
        crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], label: `${crumbs[crumbs.length - 1].label} ${breadcrumbStrings.lineItem}` };
        crumbs.push({ label: actionView, to: null });
      }
      return;
    }

    const subLabel = toTitleCase(seg);
    const nextAction = findNextNonId(rest, i);
    const isCreateNext = nextAction.toLowerCase() === 'create' || nextAction.toLowerCase() === 'add';
    const isEditNext = nextAction.toLowerCase() === 'edit';
    if (isCreateNext || isEditNext) {
      // Creating/editing a sub-resource of the current page — skip the redundant "View" crumb.
      crumbs.push({ label: `${isCreateNext ? actionCreate : actionEdit} ${singularize(subLabel)}`, to: null });
      return;
    }

    if (!viewAdded) { crumbs.push({ label: actionView, to: null }); viewAdded = true; }
    if (VIEW_SEGS.has(nextAction ?? '')) {
      crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], label: `${actionView} (${subLabel})` };
    } else {
      crumbs.push({ label: subLabel, to: null });
    }
    return;
  }
}

function resolveTabType(
  activeTab: string | null,
  tabLabel: string,
  defaultTabLabel: string | null,
  basePath: string,
): 'view' | 'list' {
  // Rule 1: no ?tab param → must be the default/first tab
  if (!activeTab) return 'view';
  // Rule 2: tab label matches the recorded first-tab label
  if (defaultTabLabel !== null && tabLabel === defaultTabLabel) return 'view';
  // Rule 3: explicit config exception for this route
  if (VIEW_TAB_EXCEPTIONS[basePath]?.has(activeTab.toLowerCase())) return 'view';
  // Rule 4: default → List
  return 'list';
}

function applyTabLabel(
  crumbs: Omit<BreadcrumbCrumb, 'kind'>[],
  activeTab: string | null, pathname: string, defaultTabLabel: string | null,
): void {
  if (crumbs.length <= 2) return;
  const { actionView } = CRUMB_LABELS;
  const last = crumbs[crumbs.length - 1];
  if (last.label !== actionView && !last.label.startsWith(`${actionView} (`)) return;
  const tabLabel = tabToLabel(activeTab, pathname, defaultTabLabel);
  if (tabLabel) {
    const basePath = '/' + (pathname || '').split('/').filter(Boolean)[0]?.toLowerCase();
    const type = resolveTabType(activeTab, tabLabel, defaultTabLabel, basePath);
    const actionLabel = type === 'view' ? actionView : 'List';
    crumbs[crumbs.length - 1] = { ...last, label: `${actionLabel} (${tabLabel})`, tab: activeTab || 'details' };
  }
}

// ── Public coordinator ────────────────────────────────────────────────────────

export function resolveBreadcrumbChain(
  pathname: string,
  activeTab: string | null,
  typeParam: string | null,
  defaultTabLabel: string | null,
  stepParam: string | null = null,
): BreadcrumbCrumb[] {
  const rawSegments = (pathname || '').split('/').filter(Boolean);
  if (!rawSegments.length) return [];

  // Normalize first segment to lowercase so lookup is case-insensitive.
  const basePath = '/' + rawSegments[0].toLowerCase();
  const lookupPath = PARENT_PATH_OVERRIDES[basePath] ?? basePath;
  const routeInfo = routeLookup[lookupPath];
  if (!routeInfo) return [];

  const { moduleLabel, pageLabel } = routeInfo;
  const deptKey = MODULE_DEPT_KEYS[moduleLabel] ?? null;
  const rest = rawSegments.slice(1).map(s => isIdSegment(s) ? ':id' : s);

  const crumbs: Omit<BreadcrumbCrumb, 'kind'>[] = [
    { label: moduleLabel, to: null, deptKey },
    { label: resolvePageLabel(basePath, lookupPath, pageLabel, rest, typeParam), to: lookupPath },
  ];

  if (rest.length) {
    appendSegmentCrumbs(crumbs, rest);
    applyTabLabel(crumbs, activeTab, pathname, defaultTabLabel);
  }

  const stepEntry = stepParam ? readStepLabelOverrides(rest[0])?.[stepParam] : undefined;
  if (typeof stepEntry === 'string') {
    crumbs.push({ label: stepEntry, to: null });
  } else if (stepEntry) {
    crumbs.push({ label: stepEntry.label, to: `${pathname}?step=${stepEntry.step}` });
    if (stepEntry.currentLabel) {
      crumbs.push({ label: stepEntry.currentLabel, to: null });
    }
  }

  return tagKinds(crumbs);
}

export function useBreadcrumbs(): BreadcrumbCrumb[] {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  return resolveBreadcrumbChain(
    pathname,
    searchParams.get('tab'),
    searchParams.get('type'),
    readDefaultTabLabel(pathname),
    searchParams.get('step'),
  );
}

export function useBreadcrumbTrail(): TrailCrumb[] {
  return useContext(BreadcrumbTrailContext);
}